import { createContext, useContext, useMemo, useState } from "react";

import Service from "../api/service";
import { useAdminContext } from "./admin/AdminContext";

const CompanyContext = createContext(null);

export const useCompanyContext = () => {
  const v = useContext(CompanyContext);
  if (!v) {
    throw new Error("useCompanyContext must be used inside CompanyProvider");
  }
  return v;
};

export const CompanyProvider = ({ children }) => {
  const { sysAdmin } = useAdminContext();
  const [company, setCompany] = useState(null);
  const [companies, setCompanies] = useState(null);

  const role = sysAdmin?.role;
  const isSuper = ["super_admin", "admin"].includes(role);

  const getAll = async (page = 1, pageSize = 50) => {
    const url = isSuper
      ? "/api/v1/company/all/?page=1&pageSize=500"
      : `/api/v1/companies/admin/?page=${page}&pageSize=${pageSize}`;
    const response = await Service.get(url);
    const payload = response?.data;
    // super_admin returns `{data: [...]}`, company admin returns the list directly.
    setCompanies(role === "super_admin" ? payload?.data : payload);
    return response;
  };

  const getOne = async (id) => {
    const response = await Service.get(`/api/v1/company/${id}`);
    setCompany(response?.data ?? null);
    return response;
  };

  const create = async (payload) =>
    Service.post("/api/v1/company/create", payload);

  const createNewAdmin = async (payload) =>
    Service.post("/api/v1/auth/admin/signup", payload);

  const update = async (company_id, payload) =>
    Service.put(`/api/v1/company/update/${company_id}`, payload);

  const updateAdminStatus = async (company_id, admin_id, admin_status) =>
    Service.put(
      `/api/v1/company/status/admin/${company_id}/${admin_id}/${admin_status}`,
      null,
    );

  const deleteCompany = async (company_id, admin_id) =>
    Service.delete(`/api/v1/company/delete/${company_id}/${admin_id}`);

  const value = useMemo(
    () => ({
      company,
      companies,
      setCompany,
      setCompanies,
      getOne,
      getAll,
      create,
      createNewAdmin,
      update,
      updateAdminStatus,
      deleteCompany,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [company, companies, role],
  );

  return (
    <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>
  );
};
